// SimplifiedLinkShortener.js - Updated with special character shortcodes
// A simplified version that works without complex Firestore indexes

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Copy,
  Trash2,
  Crown,
  ExternalLink,
  BarChart2,
  ArrowLeft,
  QrCode,
  Download,
} from "lucide-react";
import QRCode from "react-qr-code";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  setDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import styles from "./LinkShortener.module.css";
// Import the ProFeatureContext
import { useProFeature } from "../context/ProFeatureContext";

// Define max link limit
const MAX_LINKS_LIMIT = 40;

// Validate URL function
const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};

const SimplifiedLinkShortener = ({ userId, isProUser, onUpgradeClick }) => {
  // Add this line to access the ProFeatureContext
  const { showProFeatureNotification } = useProFeature();
  
  const [url, setUrl] = useState("");
  const [shortlinks, setShortlinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [recentlyCopied, setRecentlyCopied] = useState(null);

  // Views
  const [currentView, setCurrentView] = useState("shortener");
  const [selectedLink, setSelectedLink] = useState(null);

  // QR code ref for download
  const qrCodeRef = useRef(null);

  // Initial loading indicator
  const [initialLoading, setInitialLoading] = useState(true);

  // Generate a shortcode that starts with a special character - 6 chars total
  // Only using $, !, and - as special characters since _ can appear in usernames
  const generateShortCode = useCallback(() => {
    // Start with a special character (these can't be in usernames)
    const specialChars = "$!-";
    const firstChar = specialChars.charAt(Math.floor(Math.random() * specialChars.length));
    
    // Rest with alphanumeric characters (optimize for readability)
    const chars = "abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    
    // Generate 5 more characters (total with special char = 6)
    let result = firstChar;
    for (let i = 0; i < 5; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    
    return result;
  }, []);

  // Get full short URL for a link ID
  const getFullShortUrl = (id) => `https://linkist.se/${id}`;

  // Format date helper
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Truncate URL helper
  const truncateUrl = (url, maxLength = 40) => {
    if (!url) return "";
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + "...";
  };

  // Add protocol if it's missing in display
  const formatDisplayUrl = (url) => {
    if (!url) return "";
    return url;
  };

  // Mobile-friendly clipboard function with fallbacks
  const copyToClipboard = async (text) => {
    let copied = false;
    
    try {
      // Modern approach (works on most browsers)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        copied = true;
      } else {
        // Fallback for older browsers and some mobile browsers
        const textArea = document.createElement("textarea");
        textArea.value = text;
        
        // Make the textarea out of viewport
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        
        // For iOS specific behavior
        if (navigator.userAgent.match(/ipad|iphone/i)) {
          // Save current selection
          const range = document.createRange();
          range.selectNodeContents(textArea);
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range);
          textArea.setSelectionRange(0, text.length);
        } else {
          textArea.focus();
          textArea.select();
        }
        
        try {
          const successful = document.execCommand("copy");
          copied = successful;
        } catch (err) {
          console.error("Fallback copy method failed", err);
        }
        
        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error("Copy to clipboard failed:", err);
    }
    
    return copied;
  };

  // ---- SIMPLE FETCH WITHOUT COMPLEX QUERIES ----
  const fetchShortlinks = useCallback(async () => {
    console.log("[fetchShortlinks] userId =", userId);

    // If userId is missing, skip
    if (!userId) {
      console.log("[fetchShortlinks] No userId yet, returning.");
      setShortlinks([]);
      setInitialLoading(false);
      return;
    }

    setLoading(true);
    try {
      console.log("[fetchShortlinks] Getting all documents from shortlinks collection");
      
      // Simpler approach: get all documents and filter in JS
      const shortlinksRef = collection(db, "shortlinks");
      const querySnapshot = await getDocs(shortlinksRef);
      
      const links = [];
      querySnapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        
        // Only include links for this user
        if (data.userId === userId) {
          let createdAtDate;
          
          // Handle various timestamp formats
          if (data.createdAt) {
            if (typeof data.createdAt.toDate === "function") {
              createdAtDate = data.createdAt.toDate();
            } else if (data.createdAt.seconds) {
              // Firestore timestamp object with seconds & nanoseconds
              createdAtDate = new Date(data.createdAt.seconds * 1000);
            } else {
              // Fallback
              createdAtDate = new Date(data.createdAt);
            }
          } else {
            createdAtDate = new Date();
          }
          
          links.push({
            id: docSnapshot.id,
            ...data,
            createdAt: createdAtDate,
          });
        }
      });
      
      // Sort links by createdAt in JavaScript
      links.sort((a, b) => b.createdAt - a.createdAt);
      
      console.log("[fetchShortlinks] Found links for this user:", links.length);
      setShortlinks(links);
    } catch (err) {
      console.error("Error fetching shortlinks:", err);
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [userId]);

  // Fetch links when component mounts or userId changes
  useEffect(() => {
    if (userId) {
      console.log(`[LinkShortener] User ID available, triggering fetch after 2 seconds`);
      const timerId = setTimeout(() => {
        fetchShortlinks();
      }, 2000); // Extended delay to ensure auth is ready
      
      return () => clearTimeout(timerId);
    } else {
      setShortlinks([]);
      setInitialLoading(false);
    }
  }, [userId, fetchShortlinks]);

  // ----- SHORTEN -----
  const handleShortenUrl = async () => {
    if (!isProUser) {
      // Show pro feature notification without auto-redirect
      showProFeatureNotification("Upgrade to Pro to use Link Shortener");
      return;
    }
    
    setError("");
    setCopySuccess(false);

    // Check if at or over link limit
    if (shortlinks.length >= MAX_LINKS_LIMIT) {
      setError(`You've reached the maximum limit of ${MAX_LINKS_LIMIT} shortened links. Please delete some links before creating new ones.`);
      return;
    }

    if (!url.trim()) {
      setError("Please enter a URL to shorten.");
      return;
    }

    // Add protocol if missing
    let formattedUrl = url.trim();
    if (
      !formattedUrl.startsWith("http://") &&
      !formattedUrl.startsWith("https://")
    ) {
      formattedUrl = "https://" + formattedUrl;
    }

    if (!isValidUrl(formattedUrl)) {
      setError("Please enter a valid URL.");
      return;
    }

    try {
      setLoading(true);

      // Generate a short code - exactly 6 characters, starts with special char
      const shortCode = generateShortCode();
      console.log("Generated shortCode:", shortCode);
      
      const shortlinkRef = doc(db, "shortlinks", shortCode);

      // Build doc data
      const newShortlink = {
        originalUrl: formattedUrl,
        userId: userId,
        createdAt: serverTimestamp(),
        clicks: 0
      };

      // Write to Firestore
      await setDoc(shortlinkRef, newShortlink);
      console.log("Document written with ID:", shortCode);

      // Add to local state
      const tempNewLink = {
        id: shortCode,
        ...newShortlink,
        createdAt: new Date(),
      };
      
      setShortlinks((prev) => [tempNewLink, ...prev]);

      // Copy to clipboard with enhanced mobile support
      const fullShortUrl = getFullShortUrl(shortCode);
      const copied = await copyToClipboard(fullShortUrl);
      
      if (copied) {
        setCopySuccess(true);
        setRecentlyCopied(shortCode);
      } else {
        // If copy failed
        setError("Could not copy to clipboard automatically. Tap the copy button to try again.");
      }
      
      setUrl("");

      // Clear "copied" message
      setTimeout(() => {
        setCopySuccess(false);
        setRecentlyCopied(null);
      }, 2000);

      // Refresh the list
      await fetchShortlinks();
    } catch (err) {
      console.error("Error shortening URL:", err);
      setError(`Failed to shorten URL: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ----- COPY -----
  const handleCopyLink = async (shortUrl, id) => {
    if (!isProUser) {
      // Show pro feature notification without auto-redirect
      showProFeatureNotification("Upgrade to Pro to copy shortened links");
      return;
    }
    
    const copied = await copyToClipboard(shortUrl);
    
    if (copied) {
      setRecentlyCopied(id);
      setTimeout(() => setRecentlyCopied(null), 2000);
    } else {
      // If copy failed, show a manual method
      alert(`Please copy this link manually: ${shortUrl}`);
    }
  };

  // ----- DELETE SINGLE -----
  const handleDeleteLink = async (id) => {
    if (!isProUser) {
      // Show pro feature notification without auto-redirect
      showProFeatureNotification("Upgrade to Pro to manage shortened links");
      return;
    }
    
    try {
      setLoading(true);
      await deleteDoc(doc(db, "shortlinks", id));
      setShortlinks((prev) => prev.filter((link) => link.id !== id));
    } catch (err) {
      console.error("Error deleting shortlink:", err);
      setError("Failed to delete link. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // ----- CLEAR HISTORY -----
  const handleClearHistory = async () => {
    if (!isProUser) {
      // Show pro feature notification without auto-redirect
      showProFeatureNotification("Upgrade to Pro to manage shortened links");
      return;
    }
    
    if (
      !window.confirm(
        "Are you sure you want to clear your shortened links history?"
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      
      // Delete links one by one
      for (const link of shortlinks) {
        await deleteDoc(doc(db, "shortlinks", link.id));
      }
      
      setShortlinks([]);
    } catch (err) {
      console.error("Error clearing history:", err);
      setError("Failed to clear history. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // ----- ANALYTICS VIEW -----
  const showAnalytics = (link) => {
    if (!isProUser) {
      // Show pro feature notification without auto-redirect
      showProFeatureNotification("Upgrade to Pro to view link analytics and QR codes");
      return;
    }
    
    setSelectedLink(link);
    setCurrentView("analytics");
  };

  const backToShortener = () => {
    setCurrentView("shortener");
    setSelectedLink(null);
  };

  const handleDownloadQR = () => {
    if (!isProUser) {
      // Show pro feature notification without auto-redirect
      showProFeatureNotification("Upgrade to Pro to download QR codes");
      return;
    }
    
    if (!selectedLink) return;
    const svg = qrCodeRef.current;
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      canvas.width = 1024;
      canvas.height = 1024;
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const downloadLink = document.createElement("a");
      downloadLink.download = `qrcode-${selectedLink.id}.png`;
      downloadLink.href = canvas.toDataURL("image/png");
      downloadLink.click();
    };
    img.src =
      "data:image/svg+xml;base64," +
      btoa(unescape(encodeURIComponent(svgData)));
  };

  // Loading state if we're still fetching user data for the very first time
  if (initialLoading) {
    return (
      <div className={styles.shortenerContainer}>
        <div className={styles.loadingState}>
          <p>Loading your shortlinks...</p>
        </div>
      </div>
    );
  }

  // ---- RENDER VIEWS ----
  const renderShortenerView = () => (
    <>
      <div className={styles.shortenerHeader}>
        <h1>Link Shortener</h1>
        <div className={styles.inputContainer}>
          <div className={styles.inputWrapper}>
            <input
              type="text"
              className={styles.urlInput}
              placeholder="Paste your long URL here"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={!isProUser || loading || shortlinks.length >= MAX_LINKS_LIMIT}
            />
            <button
              className={styles.shortenButton}
              onClick={handleShortenUrl}
              disabled={!isProUser || loading || shortlinks.length >= MAX_LINKS_LIMIT}
            >
              {loading ? "Processing..." : shortlinks.length >= MAX_LINKS_LIMIT ? "Limit Reached" : "Shorten & Copy"}
            </button>
          </div>

          {error && <div className={styles.errorMessage}>{error}</div>}
          {copySuccess && (
            <div className={styles.successMessage}>
              Link copied to clipboard!
            </div>
          )}
          {shortlinks.length >= MAX_LINKS_LIMIT && !error && (
            <div className={styles.errorMessage}>
              You've reached the maximum limit of {MAX_LINKS_LIMIT} shortened links. Please delete some links to create new ones.
            </div>
          )}
        </div>

        <div className={styles.clearContainer}>
          <button
            className={styles.clearButton}
            onClick={handleClearHistory}
            disabled={!isProUser || loading || shortlinks.length === 0}
          >
            Clear History
          </button>
        </div>
      </div>

      {!isProUser && (
        <div className={styles.proOverlay}>
          <div className={styles.proContent}>
            <Crown size={32} className={styles.proCrown} />
            <h2>Pro Feature</h2>
            <p>Link shortening is available exclusively to Pro users.</p>
            <button className={styles.upgradeButton} onClick={onUpgradeClick}>
              Upgrade to Pro
            </button>
          </div>
        </div>
      )}

      <div className={styles.historyContainer}>
        <h2 className={styles.historyTitle}>
          Your Shortened Links
          <span className={styles.historyCount}>{shortlinks.length} / {MAX_LINKS_LIMIT}</span>
        </h2>

        {shortlinks.length === 0 ? (
          <div className={styles.emptyHistory}>
            <p>You haven't shortened any links yet.</p>
          </div>
        ) : (
          <div className={styles.linksList}>
            {shortlinks.map((link) => (
              <div key={link.id} className={styles.linkItem}>
                <div className={styles.linkDetails}>
                  <div className={styles.shortUrlContainer}>
                    <span className={styles.shortUrl}>
                      linkist.se/{link.id}
                    </span>
                    <button
                      className={styles.copyButton}
                      onClick={() =>
                        handleCopyLink(getFullShortUrl(link.id), link.id)
                      }
                      disabled={!isProUser}
                    >
                      {recentlyCopied === link.id ? (
                        "Copied!"
                      ) : (
                        <Copy size={16} />
                      )}
                    </button>
                  </div>

                  <div className={styles.originalUrlContainer}>
                    <span className={styles.originalUrl}>
                      {truncateUrl(formatDisplayUrl(link.originalUrl))}
                    </span>
                    <a
                      href={link.originalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.visitButton}
                    >
                      <ExternalLink size={14} />
                    </a>
                  </div>

                  <div className={styles.linkMeta}>
                    <span className={styles.createDate}>
                      Created: {formatDate(link.createdAt)}
                    </span>
                    {link.clicks !== undefined && (
                      <span className={styles.clickCount}>
                        {link.clicks} clicks
                      </span>
                    )}
                  </div>
                </div>

                <div className={styles.actionButtons}>
                  <button
                    className={styles.analyticsButton}
                    onClick={() => showAnalytics(link)}
                    disabled={!isProUser}
                    title="View Analytics"
                    aria-label="View Analytics"
                  >
                    <BarChart2 size={18} />
                  </button>
                  <button
                    className={styles.qrButton}
                    onClick={() => showAnalytics(link)}
                    disabled={!isProUser}
                    title="View QR Code"
                    aria-label="View QR Code"
                  >
                    <QrCode size={18} />
                  </button>
                  <button
                    className={styles.deleteButton}
                    onClick={() => handleDeleteLink(link.id)}
                    disabled={!isProUser}
                    title="Delete Link"
                    aria-label="Delete Link"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );

  // Updated Analytics View with improved mobile responsiveness
  const renderAnalyticsView = () => {
    if (!selectedLink) return null;

    return (
      <div className={styles.analyticsView}>
        <div className={styles.analyticsHeader}>
          <button className={styles.backButton} onClick={backToShortener}>
            <ArrowLeft size={16} /> Back
          </button>
          <h1>Link Stats</h1>
        </div>

        <div className={styles.analyticsCard}>
          <div className={styles.analyticsMeta}>
            <div className={styles.analyticsShortUrl}>
              linkist.se/{selectedLink.id}
              <button
                className={styles.copyButton}
                onClick={() =>
                  handleCopyLink(
                    getFullShortUrl(selectedLink.id),
                    selectedLink.id
                  )
                }
              >
                {recentlyCopied === selectedLink.id ? (
                  "Copied!"
                ) : (
                  <Copy size={16} />
                )}
              </button>
            </div>
            <div className={styles.analyticsOriginalUrl}>
              {selectedLink.originalUrl}
            </div>
            <div className={styles.analyticsDate}>
              Created: {formatDate(selectedLink.createdAt)}
            </div>
          </div>

          <div className={styles.analyticsStats}>
            <div className={styles.statCard}>
              <div className={styles.statValue}>{selectedLink.clicks || 0}</div>
              <div className={styles.statLabel}>Total Clicks</div>
            </div>

            <div className={styles.qrCodeCard}>
              <div className={styles.qrCodeContainer}>
                <QRCode
                  ref={qrCodeRef}
                  value={getFullShortUrl(selectedLink.id)}
                  size={128}
                  level="H"
                  className={styles.qrCode}
                />
              </div>
              <button
                className={styles.downloadQrButton}
                onClick={handleDownloadQR}
              >
                <Download size={16} /> Download QR
              </button>
            </div>
          </div>

          <div className={styles.analyticsMessage}>
            {selectedLink.clicks ? (
              <p>More detailed analytics coming soon!</p>
            ) : (
              <p>No clicks yet. Share your link to start collecting stats!</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.shortenerContainer}>
      {currentView === "shortener"
        ? renderShortenerView()
        : renderAnalyticsView()}
    </div>
  );
};

export default SimplifiedLinkShortener;